import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const CurrencyContext = createContext(null);

const CURRENCY_SYMBOLS = {
  EUR: "€",
  USD: "$",
  GBP: "£",
  JPY: "¥",
  CHF: "CHF ",
  CAD: "C$",
  AUD: "A$",
  KRW: "₩",
  CNY: "¥",
};

const STORAGE_KEY = "cartonomie_currency";

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    return (typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY)) || "EUR";
  });
  // rates are base=EUR (i.e. 1 EUR = rates[X] X)
  const [rates, setRates] = useState({ EUR: 1, USD: 1.08, GBP: 0.85, JPY: 168, CHF: 0.94, CAD: 1.47, AUD: 1.63, KRW: 1470, CNY: 7.85 });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api.get("/fx/rates")
      .then((r) => {
        const data = r.data || {};
        const merged = { EUR: 1, ...(data.rates || {}) };
        setRates(merged);
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c);
    try { localStorage.setItem(STORAGE_KEY, c); } catch (e) { /* ignore */ }
  }, []);

  const convert = useCallback((value, fromCurrency = "EUR") => {
    if (value === null || value === undefined || isNaN(value)) return null;
    if (!rates[fromCurrency] || !rates[currency]) return value;
    // First convert source -> EUR (since rates are base=EUR)
    const valueInEUR = fromCurrency === "EUR" ? Number(value) : Number(value) / rates[fromCurrency];
    if (currency === "EUR") return valueInEUR;
    return valueInEUR * rates[currency];
  }, [rates, currency]);

  const format = useCallback((value, fromCurrency = "EUR") => {
    const converted = convert(value, fromCurrency);
    if (converted === null) return "—";
    const sym = CURRENCY_SYMBOLS[currency] || (currency + " ");
    const decimals = currency === "JPY" || currency === "KRW" ? 0 : 2;
    return `${sym}${Number(converted).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  }, [convert, currency]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, ready, convert, format, symbols: CURRENCY_SYMBOLS }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // safe fallback when used outside provider (e.g. tests)
    return {
      currency: "EUR",
      setCurrency: () => {},
      rates: { EUR: 1 },
      ready: false,
      convert: (v) => v,
      format: (v, c = "EUR") => v == null ? "—" : `${CURRENCY_SYMBOLS[c] || c + " "}${Number(v).toFixed(2)}`,
      symbols: CURRENCY_SYMBOLS,
    };
  }
  return ctx;
}

export const SUPPORTED_CURRENCIES = ["EUR", "USD", "GBP", "JPY", "CHF", "CAD", "AUD", "KRW", "CNY"];
